import { cn } from "@/lib/cn";

/**
 * DRB-72 (SEO HIGH) — reserved space for content that has not arrived.
 *
 * Measured on the live site at 390x844 before this existed:
 *
 *   /                              CLS 1.0133   (one shift of 1.0 at 891ms)
 *   /products/cbd-pure-1200mg      CLS 0.4579
 *
 * against Google's 0.1 "good" threshold. The cause is the same everywhere: a component returns
 * `null` or a centred 40vh spinner while it fetches, then swaps in several viewport-heights of
 * real content. Nothing reserves the height, so everything below jumps.
 *
 * A spinner is NOT a skeleton. `LoadingBlock` is a fixed 40vh regardless of what it stands in for,
 * so it guarantees a shift the moment the real content is taller or shorter — which is always. A
 * skeleton is only useful if it is roughly the SHAPE of what replaces it.
 *
 * `aria-hidden` because this is decorative: a screen reader should hear the live region announce
 * the real content, not a description of grey boxes.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "animate-pulse rounded-soft bg-night-blue/10 motion-reduce:animate-none",
        className,
      )}
    />
  );
}

/** A line of placeholder text. `w` lets a block of lines look like a paragraph, not a grid. */
export function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  const widths = ["w-full", "w-11/12", "w-4/5", "w-full", "w-3/4"];
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn("h-4", widths[i % widths.length])} />
      ))}
    </div>
  );
}
